import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  /** Zorunlu alanlar yıldızlanmaz; opsiyoneller "(isteğe bağlı)" ile işaretlenir. */
  optional?: boolean;
  hint?: string;
  /** Alan altında, kırmızı zemin değil kırmızı metin olarak gösterilir. */
  error?: string;
  /** `prefix`/`suffix` HTML attribute'larıyla çakıştığı için ayrı adlandırıldı. */
  leading?: ReactNode;
  trailing?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    { className, label, optional, hint, error, leading, trailing, id, ...props },
    ref,
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const describedBy = error
      ? `${inputId}-error`
      : hint
        ? `${inputId}-hint`
        : undefined;

    return (
      <div className="w-full">
        {label ? (
          <label
            htmlFor={inputId}
            className="mb-1.5 block text-sm font-medium text-ink"
          >
            {label}
            {optional ? (
              <span className="ml-1.5 font-normal text-ink-subtle">
                (isteğe bağlı)
              </span>
            ) : null}
          </label>
        ) : null}

        <div
          className={cn(
            'flex items-center gap-2 rounded-sm border bg-surface px-3.5',
            'transition-colors duration-fast ease-flow',
            'focus-within:border-leaf-700 focus-within:ring-2 focus-within:ring-leaf-100',
            error ? 'border-danger' : 'border-line hover:border-line-strong',
          )}
        >
          {leading ? (
            <span className="shrink-0 text-ink-subtle">{leading}</span>
          ) : null}

          <input
            ref={ref}
            id={inputId}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            className={cn(
              'h-11 w-full min-w-0 bg-transparent text-base text-ink',
              'placeholder:text-ink-subtle focus:outline-none',
              'disabled:cursor-not-allowed disabled:opacity-50',
              className,
            )}
            {...props}
          />

          {trailing ? (
            <span className="shrink-0 text-ink-subtle">{trailing}</span>
          ) : null}
        </div>

        {error ? (
          <p id={`${inputId}-error`} className="mt-1.5 text-xs text-danger">
            {error}
          </p>
        ) : hint ? (
          <p id={`${inputId}-hint`} className="mt-1.5 text-xs text-ink-muted">
            {hint}
          </p>
        ) : null}
      </div>
    );
  },
);

Input.displayName = 'Input';
